import styled, { css } from 'styled-components';
import { TypographyMixins, typographyMixins } from './typography';
import { colors, Color } from '@ardoq/design-tokens';
import { ReactNode, useRef, useState } from 'react';
import { Tooltip } from '@ardoq/popover2025';
import { ellipsisCSS, multiLineEllipsisCSS } from '@ardoq/style-helpers';

export type TextColor = (typeof textColorTokenList)[number];

type StyledTextProps = {
  $variant?: TypographyMixins;
  $marginReset?: boolean;
  $align?: 'left' | 'center' | 'right';
  $color?: TextColor;
  $truncate?: boolean | number;
};

const StyledText = styled.div<StyledTextProps>`
  ${({ $color }) =>
    $color &&
    css`
      color: ${colors[$color]};
    `}
  ${({ $variant }) => $variant && typographyMixins[$variant]}
  ${({ $marginReset }) => $marginReset && marginResetMixin}
  ${({ $align }) => $align && `text-align: ${$align};`}
  ${({ $truncate }) =>
    $truncate === true
      ? ellipsisCSS
      : typeof $truncate === 'number' && multiLineEllipsisCSS($truncate)}
`;

// NOTE: some elements may have base margins set (by, for example, Bootstrap)
const marginResetMixin = `
  margin: 0;
`;

const isOverflowing = (el: HTMLElement, multiLine: boolean): boolean =>
  multiLine
    ? el.scrollHeight > el.clientHeight
    : el.scrollWidth > el.clientWidth;

const TruncatedText = ({
  children,
  tooltipText,
  color,
  variant,
  align,
  truncate,
  ...props
}: TextProps) => {
  const isOnlyEllipsis =
    truncate === 'only-ellipsis' ||
    (typeof truncate === 'object' && truncate.onlyEllipsis === true);
  const lines = typeof truncate === 'object' ? truncate.lines : undefined;
  const isMultiLine = lines !== undefined;

  const ref = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseOver = () => {
    if (typeof children !== 'string' && tooltipText === undefined) {
      return;
    }
    if (ref.current && isOverflowing(ref.current, isMultiLine)) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <StyledText
      ref={ref}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      $color={color}
      $variant={variant}
      $align={align}
      $truncate={lines ?? true}
      {...props}
    >
      {showTooltip && !isOnlyEllipsis && (
        <Tooltip
          isOpen={showTooltip}
          onClose={() => setShowTooltip(false)}
          anchorRef={ref}
        >
          {tooltipText ?? children}
        </Tooltip>
      )}
      {children}
    </StyledText>
  );
};

type HeaderProps = {
  color?: TextColor;
  align?: 'left' | 'center' | 'right';
  title?: string;
  children: ReactNode;
  id?: string;
};

export const Header1 = ({ color, align, ...rest }: HeaderProps) => {
  return (
    <StyledText
      as="h1"
      $variant="header1"
      $marginReset
      $align={align}
      $color={color}
      {...rest}
    />
  );
};

export const Header2 = ({ color, align, ...rest }: HeaderProps) => {
  return (
    <StyledText
      as="h2"
      $variant="header2"
      $marginReset
      $align={align}
      $color={color}
      {...rest}
    />
  );
};

export const Header3 = ({ color, align, ...rest }: HeaderProps) => {
  return (
    <StyledText
      as="h3"
      $variant="header3"
      $marginReset
      $align={align}
      $color={color}
      {...rest}
    />
  );
};

export const Header4 = ({ color, align, ...rest }: HeaderProps) => {
  return (
    <StyledText
      as="h4"
      $variant="header4"
      $marginReset
      $align={align}
      $color={color}
      {...rest}
    />
  );
};

export const Header5 = ({ color, align, ...rest }: HeaderProps) => {
  return (
    <StyledText
      as="h5"
      $variant="header5"
      $marginReset
      $align={align}
      $color={color}
      {...rest}
    />
  );
};

type AllowedHTMLAttributes = Pick<React.HTMLAttributes<HTMLElement>, 'id'>;

export type TextProps = {
  variant?:
    | 'text1'
    | 'text1Italic'
    | 'text1Bold'
    | 'text2'
    | 'text2Bold'
    | 'caption'
    | 'captionBold';
  color?: TextColor;
  align?: 'left' | 'center' | 'right';
  children: ReactNode;
  /**
   * Text to display in a tooltip when the text is truncated. Should only be
   * used when children is not a plain string.
   */
  tooltipText?: string;
  truncate?:
    | boolean
    | 'only-ellipsis'
    | { lines: number; onlyEllipsis?: boolean };
} & AllowedHTMLAttributes;

export const textColorTokenList = [
  'textDefault',
  'textModerate',
  'textSubtle',
  'textDisabled',
  'textOnInverted',
  'textOnInvertedSubtle',
  'textOnInvertedStrong',
  'textOnInvertedDisabled',
  'textError',
  'textSuccess',
  'textInfo',
  'textAction',
  'textWarning',
  'textBrand',
] as const satisfies Color[];

export const InlineText = ({ truncate, tooltipText, ...props }: TextProps) => {
  if (truncate) {
    return (
      <TruncatedText tooltipText={tooltipText} truncate={truncate} {...props} />
    );
  }

  const { color, variant, align, ...rest } = props;

  return (
    <StyledText
      $color={color}
      $variant={variant}
      $align={align}
      {...rest}
      as="span"
    />
  );
};

export const Paragraph = ({ color, variant, align, ...rest }: TextProps) => {
  return (
    <StyledText
      $color={color}
      $variant={variant}
      $align={align}
      $marginReset
      {...rest}
      as="p"
    />
  );
};

export const Text = ({ truncate, tooltipText, ...props }: TextProps) => {
  if (truncate) {
    return (
      <TruncatedText tooltipText={tooltipText} truncate={truncate} {...props} />
    );
  }

  const { color, variant, align, ...rest } = props;

  return (
    <StyledText
      $color={color}
      $variant={variant}
      $align={align}
      {...rest}
      as="div"
    />
  );
};
